import type { MapDef } from '@brig/rules-engine';
import { landPotential, SHORE } from './terrain.ts';

/**
 * The map styles and the engraved chart's palette (the chart itself is drawn in chart.ts),
 * kept free of PixiJS so the minimap and the board's shell can use them without loading it.
 */

/** How the map itself is drawn: an engraved sea chart, or watercolour terrain with relief. */
export type MapStyle = 'chart' | 'watercolour';

export const CHART = {
  ink: 0x604d37,
  /** Paper: the lettering's halo and the plaques. */
  paper: 0xf1e6cb,
  /** The sea on the chart: pale, for the islands' tint to stand out against. */
  sea: 0xcfe6ee,
  /** The parchment strip round the grid. */
  strip: 0xe6d2a6,
  land: 0xd8c49a,
  foxing: 0x987341,
  frame: 0x927244,
  /** The open sea round the sheet, its currents and the foam on its rocks. */
  table: '#6fb8d6',
  wave: 0x2e7fa8,
  foam: 0xf8fbfb,
} as const;

/** The parchment strip round the grid, in cells; the coordinates stand on the sea beyond it. */
export const SHEET_MARGIN = 0.35;
/** How far the chart reaches past the grid, lettering included, in cells. */
export const CHART_REACH = SHEET_MARGIN + 0.9;

/** The chart from above, small, for the minimap: paper, tinted land, an inked coast. */
export function chartMinimap(map: MapDef, res = 6): HTMLCanvasElement {
  const p = landPotential(map);
  const canvas = document.createElement('canvas');
  canvas.width = map.width * res;
  canvas.height = map.height * res;
  const ctx = canvas.getContext('2d');
  if (!ctx) return canvas;
  const img = ctx.createImageData(canvas.width, canvas.height);
  const rgb = (c: number) => [(c >> 16) & 255, (c >> 8) & 255, c & 255] as const;
  const [paper, land, ink] = [rgb(CHART.paper), rgb(CHART.land), rgb(CHART.ink)];
  for (let y = 0; y < canvas.height; y++) {
    for (let x = 0; x < canvas.width; x++) {
      const q = p((x + 0.5) / res, (y + 0.5) / res) - SHORE;
      const c = Math.abs(q) < 0.035 ? ink : q > 0 ? land : paper;
      const o = (y * canvas.width + x) * 4;
      img.data[o] = c[0];
      img.data[o + 1] = c[1];
      img.data[o + 2] = c[2];
      img.data[o + 3] = 255;
    }
  }
  ctx.putImageData(img, 0, 0);
  return canvas;
}
